import { VISIT_STATUS_LABEL } from "../constants.ts";
import type { ScheduleCur, Ticket } from "../types.ts";

function serialTail(serial: string | null | undefined): string {
  const s = (serial || "").trim();
  if (!s) return "";
  return s.length > 4 ? s.slice(-4) : s;
}

/** 会社名の代替表示（未登録・未入力時） */
export function ticketCustomerLabel(t: Ticket): string {
  const company = (t.company || "").trim();
  if (company) return company;
  const contact = (t.contact_name || "").trim();
  if (contact) return contact;
  const partner = (t.partner_code || "").trim();
  if (partner) return `取引先 ${partner}`;
  return "客先未登録";
}

/**
 * 一覧・詳細のメイン見出し。
 * - 手入力 title がある → 内容を優先
 * - title が空 → 顧客ラベル（…下4） / 顧客ラベル / …下4 / （無題）
 */
export function ticketDisplayTitle(t: Ticket): string {
  const title = (t.title || "").trim();
  const customer = ticketCustomerLabel(t);
  const tail = serialTail(t.unit_serial);

  if (title) return title;

  if (customer !== "客先未登録" && tail) return `${customer}（…${tail}）`;
  if (customer !== "客先未登録") return customer;
  if (tail) return `…${tail}`;
  return "（無題）";
}

/** メイン見出しの下に付ける補助行 */
export function ticketDisplayContext(t: Ticket): string | null {
  const parts: string[] = [];
  const company = (t.company || "").trim();
  const tail = serialTail(t.unit_serial);
  const product = (t.product || "").trim();
  const contact = (t.contact_name || "").trim();
  const partner = (t.partner_code || "").trim();

  if (company) {
    parts.push(company);
  } else {
    if (contact) parts.push(`担当 ${contact}`);
    if (partner) parts.push(`取引先 ${partner}`);
    if (!contact && !partner) parts.push("客先未登録");
  }
  if (tail) parts.push(`…${tail}`);
  if (product) parts.push(product);
  return parts.length ? parts.join(" · ") : null;
}

/** チケットに登録された連絡先メール（contact_emails 優先、なければ contact_email）。重複はマージ。 */
export function ticketContactEmails(ticket: Ticket): string[] {
  const raw: string[] = [];
  if (ticket.contact_emails && ticket.contact_emails.length > 0) {
    raw.push(...ticket.contact_emails.map((e) => e.trim()).filter(Boolean));
  } else {
    const single = (ticket.contact_email || "").trim();
    if (single) raw.push(single);
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of raw) {
    const k = e.toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      out.push(e.toLowerCase());
    }
  }
  return out;
}

export function contactSummary(ticket: Ticket): string {
  const emails = ticketContactEmails(ticket);
  const phones = ticket.contact_phones || [];
  const parts: string[] = [];
  if (emails.length > 0) parts.push(`メール ${emails.length}件`);
  if (phones.length > 0) parts.push(`電話 ${phones.length}件`);
  return parts.length > 0 ? parts.join(" · ") : "未登録";
}

export function scheduleSummary(cur: ScheduleCur): string {
  const vs = cur.visit_status || "unscheduled";
  if (vs === "unscheduled" && !cur.scheduled_at && !cur.scheduled_tech) return "未定";
  const parts: string[] = [];
  if (cur.scheduled_at) {
    parts.push(new Date(cur.scheduled_at).toLocaleString("ja-JP", {
      month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }));
  }
  parts.push(VISIT_STATUS_LABEL[vs] || vs);
  if (cur.scheduled_tech) parts.push(cur.scheduled_tech);
  return parts.join(" · ");
}

export function intakeSummary(ticket: Ticket): string {
  if (ticket.description) {
    return ticket.description.slice(0, 40) + (ticket.description.length > 40 ? "…" : "");
  }
  return `添付 ${(ticket.attachments || []).length}件`;
}

/** 詳細ヘッダの1行サマリ（改善 C）。
 *  製品・機番・会社・設置日を「・」でつないだ1行。チップ群は「詳細 ▾」の裏へ畳む。 */
export function metaSummaryLine(t: Ticket): string {
  const parts: string[] = [];
  const product = (t.product || "").trim();
  const serial = (t.unit_serial || "").trim() || (t.device_mac || "").trim();
  const company = (t.company || "").trim();
  const install = (t.install_date || "").trim();

  if (product) parts.push(product);
  if (serial) parts.push(serial);
  if (company) parts.push(company);
  if (install) parts.push(`設置 ${install}`);
  if (!parts.length) {
    const partner = (t.partner_code || "").trim();
    if (partner) parts.push(`取引先 ${partner}`);
  }
  return parts.length ? parts.join(" ・ ") : "機体・客先の登録なし";
}
